import { useEffect, useState } from 'react';

import { Http } from '@mygpt/app/http';

import {
  extOf,
  fileNameFromDisposition,
  FileViewerKind,
  kindOfExt,
  kindOfZipEntries,
  LEGACY_OFFICE_EXT,
  looksLikeText,
  sniffMagic,
} from './registry';

export type FileBytesState =
  | { status: 'loading' }
  | { status: 'error'; error: unknown }
  | { status: 'unsupported'; data: ArrayBuffer; ext: string; fileName: string }
  | { status: 'ready'; data: ArrayBuffer; ext: string; fileName: string; kind: FileViewerKind };

const fetchBytes = async (fileId: string, toPdf: boolean) => {
  const response = await Http.use().get<Blob>(`/embeddings/files/${fileId}/download${toPdf ? '?to_pdf=true' : ''}`, {
    responseType: 'blob',
  });
  const header = (response.headers as Record<string, string | undefined>)['content-disposition'];
  return { data: await response.data.arrayBuffer(), headerName: fileNameFromDisposition(header) };
};

/**
 * 按格式嗅探字节：文件名没后缀 / 后缀不认识时的兜底。
 * zip 容器要看目录，这里懒加载 JSZip（docx / pptx 引擎本来也要它）。
 */
const sniff = async (data: ArrayBuffer): Promise<{ kind: FileViewerKind; ext: string } | 'ole' | undefined> => {
  const magic = sniffMagic(data);
  if (magic === 'ole') return 'ole';
  if (magic === 'zip') {
    const { default: JSZip } = await import('jszip');
    const zip = await JSZip.loadAsync(data.slice(0));
    return kindOfZipEntries(Object.keys(zip.files));
  }
  if (magic === 'pdf') return { kind: 'pdf', ext: 'pdf' };
  if (magic === 'image') return { kind: 'image', ext: '' };
  if (looksLikeText(data)) return { kind: 'text', ext: 'txt' };
  return undefined;
};

/**
 * 拉取知识库原文件字节并判定引擎种类。
 *
 * 判定顺序：传入的文件名后缀 → 响应头 Content-Disposition 的文件名后缀 → 字节嗅探。
 * 旧版 doc / xls / ppt（后缀或 OLE 魔数）浏览器解析不了，改请求 to_pdf=true 交给 pdf 引擎。
 * 下载接口走 axios 带 Authorization（<img src> / pdf.js 直连会 401），拿回来的是 blob。
 */
export const useFileBytes = (fileId: string, fileName: string): FileBytesState => {
  const [state, setState] = useState<FileBytesState>({ status: 'loading' });

  useEffect(() => {
    if (!fileId) return undefined;
    let alive = true;
    setState({ status: 'loading' });

    (async () => {
      try {
        const givenExt = extOf(fileName);
        const legacyByName = LEGACY_OFFICE_EXT.has(givenExt);
        let { data, headerName } = await fetchBytes(fileId, legacyByName);
        if (!alive) return;

        let name = fileName || headerName || fileId;
        let ext = legacyByName ? 'pdf' : givenExt || extOf(headerName);
        let kind = kindOfExt(ext);

        if (!kind) {
          const sniffed = await sniff(data);
          if (sniffed === 'ole') {
            // 旧版 Office 二进制：后端转 PDF 再看
            ({ data } = await fetchBytes(fileId, true));
            if (!alive) return;
            ext = 'pdf';
            kind = 'pdf';
          } else if (sniffed) {
            ({ kind, ext } = sniffed);
          }
        }

        if (!kind) {
          setState({ status: 'unsupported', data, ext, fileName: name });
          return;
        }
        if (!extOf(name) && ext) name = `${name}.${ext}`;
        setState({ status: 'ready', data, ext, fileName: name, kind });
      } catch (error) {
        if (!alive) return;
        console.error('文件预览：下载文件失败', error);
        setState({ status: 'error', error });
      }
    })();

    return () => {
      alive = false;
    };
  }, [fileId, fileName]);

  return state;
};
